import React, { useState, useEffect } from 'react';
import { getApiUrl, API_ENDPOINTS } from '../config/apiConfig';
import { apiHelper } from '../utils/apiHelper';
import styled from 'styled-components';
import { Link } from 'react-router-dom';

const AdminContainer = styled.div`
  padding: 50px 5%;
  min-height: 100vh;
  background-color: #0b0614;
`;

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 30px;
    gap: 20px;
    flex-wrap: wrap;

    @media (max-width: 768px) {
        flex-direction: column;
        align-items: stretch;
    }
`;

const Title = styled.h1`
  font-size: 3rem;
  font-weight: 800;
  background: linear-gradient(90deg, var(--gradient-cyan), var(--gradient-pink));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  
  @media (max-width: 768px) {
    font-size: 2rem;
    text-align: center;
  }
`;

const SearchContainer = styled.div`
    display: flex;
    gap: 15px;
    align-items: center;
    flex: 1;
    max-width: 400px;
    margin: 0 20px;
    
    @media (max-width: 768px) {
        max-width: 100%;
        margin: 10px 0;
    }
`;

const SearchInput = styled.input`
    flex: 1;
    padding: 12px 16px;
    font-size: 1rem;
    background: var(--glass-bg);
    border: 1px solid var(--border-color);
    border-radius: 10px;
    color: var(--primary-color);
    outline: none;
    transition: border-color 0.3s ease;
    
    &:focus {
        border-color: var(--gradient-pink);
    }
    
    &::placeholder {
        color: var(--secondary-color);
    }
`;

const AddButton = styled(Link)`
    padding: 12px 25px;
    font-size: 1rem;
    font-weight: 600;
    border: none;
    border-radius: 10px;
    background: var(--gradient-pink);
    color: white;
    cursor: pointer;
    text-decoration: none;
    transition: all 0.3s ease;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 5px 15px rgba(255, 0, 127, 0.3);
    }
`;

const ContentTable = styled.table`
    width: 100%;
    border-collapse: collapse;
    color: var(--primary-color);
`;

const Th = styled.th`
    text-align: left;
    padding: 15px;
    border-bottom: 2px solid var(--gradient-pink);
`;

const Td = styled.td`
    padding: 15px;
    border-bottom: 1px solid var(--border-color);
    vertical-align: middle;

    img {
        width: 50px;
        border-radius: 5px;
    }
`;

const ActionButtons = styled.div`
    display: flex;
    gap: 10px;
`;

const EditButton = styled(Link)`
    padding: 8px 15px;
    border-radius: 5px;
    background: var(--gradient-cyan);
    color: #111;
    text-decoration: none;
    font-weight: 600;
    transition: all 0.3s ease;

    &:hover {
        transform: translateY(-1px);
        box-shadow: 0 3px 10px rgba(78, 205, 196, 0.3);
    }
`;

const DeleteButton = styled.button`
    padding: 8px 15px;
    border-radius: 5px;
    background: #e94057;
    color: white;
    border: none;
    cursor: pointer;
    font-weight: 600;
    transition: all 0.3s ease;

    &:hover {
        transform: translateY(-1px);
        box-shadow: 0 3px 10px rgba(233, 64, 87, 0.3);
    }
`;

const MediaTypeBadge = styled.span`
    padding: 5px 12px;
    border-radius: 20px;
    font-size: 0.8rem;
    font-weight: 600;
    display: inline-block;
    text-align: center;
    min-width: 70px;
    
    ${props => {
        switch (props.type) {
            case 'anime':
                return `
                    background: linear-gradient(135deg, #ff6b6b, #ee5a24);
                    color: white;
                `;
            case 'tv':
                return `
                    background: linear-gradient(135deg, #4ecdc4, #00b894);
                    color: #111;
                `;
            case 'movie':
                return `
                    background: linear-gradient(135deg, #45b7d1, #0984e3);
                    color: white;
                `;
            default:
                return `
                    background: #636e72;
                    color: white;
                `;
        }
    }}
`;

const LoadingText = styled.div`
    text-align: center;
    padding: 50px;
    color: var(--secondary-color);
    font-size: 1.2rem;
`;

const ErrorText = styled.div`
    text-align: center;
    padding: 50px;
    color: #e94057;
    font-size: 1.2rem;
`;

const StatsContainer = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 20px;
    margin-bottom: 30px;
`;

const StatCard = styled.div`
    background: var(--glass-bg);
    border: 1px solid var(--border-color);
    border-radius: 12px;
    padding: 20px;
    text-align: center;
    
    h3 {
        font-size: 2rem;
        margin: 0;
        background: linear-gradient(90deg, var(--gradient-cyan), var(--gradient-pink));
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
    }
    
    p {
        margin: 5px 0 0 0;
        color: var(--secondary-color);
        font-size: 0.9rem;
    }
`;

const AdminPage = () => {
    const [content, setContent] = useState([]);
    const [filteredContent, setFilteredContent] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [stats, setStats] = useState({
        total: 0,
        anime: 0,
        tv: 0,
        movie: 0
    });
    
    useEffect(() => {
        fetchContent();
    }, []);

    // Arama filtresi
    useEffect(() => {
        if (!searchTerm) {
            setFilteredContent(content);
        } else {
            const filtered = content.filter(item => 
                item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                item.original_title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                item.tmdb_id.toString().includes(searchTerm) ||
                getMediaTypeLabel(item).toLowerCase().includes(searchTerm.toLowerCase())
            );
            setFilteredContent(filtered);
        }
    }, [content, searchTerm]);

    const fetchContent = async () => {
        try {
            setLoading(true);
            const response = await fetch(getApiUrl(API_ENDPOINTS.CHECK_CONTENT));
            
            if (!response.ok) {
                throw new Error('İçerikler çekilemedi');
            }
            
            const contentList = await response.json();
            setContent(contentList);
            
            // İstatistikleri hesapla
            const statsData = {
                total: contentList.length,
                anime: contentList.filter(item => item.media_type === 'anime').length,
                tv: contentList.filter(item => item.media_type === 'tv').length,
                movie: contentList.filter(item => item.media_type === 'movie').length
            };
            setStats(statsData);
            
            setError(null);
        } catch (err) {
            console.error('İçerik çekme hatası:', err);
            setError('İçerikler yüklenirken bir hata oluştu');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id, title) => {
        if (window.confirm(`"${title}" içeriğini silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.`)) {
            try {
                const response = await apiHelper.deleteContent({ id });

                if (!response.ok) {
                    const errorText = await response.text();
                    console.error('Silme hatası:', errorText);
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const result = await response.json();
                
                if (result.success) {
                    alert(`"${title}" başarıyla silindi.`);
                    fetchContent();
                } else {
                    throw new Error(result.error || 'Silme işlemi başarısız');
                }
            } catch (error) {
                console.error("Silme hatası:", error);
                alert(`İçerik silinirken bir hata oluştu: ${error.message}`);
            }
        }
    };

    const getMediaTypeLabel = (item) => {
        if (item.media_type === 'anime') {
            return 'Anime';
        } else if (item.media_type === 'tv') {
            return 'Dizi';
        } else if (item.media_type === 'movie') {
            return 'Film';
        }
        return item.media_type || 'Bilinmiyor';
    };

        const getPosterUrl = (posterPath) => {
        if (!posterPath) return '/placeholder-image.jpg';

        if (posterPath.includes('/proxy-image.php')) return posterPath;

        return getImageUrl(posterPath, 'w154'); // küçük boy poster
        };
    // Proxy image URL oluşturma fonksiyonunu ekle (diğer import'ların altına)
        const getImageUrl = (path, size = 'w500') => {
        if (!path) return '/placeholder-image.jpg';
        
        if (path.includes('/proxy-image.php')) return path;

        // Tek bir proxy yolu kullanıyoruz
        return `${getApiUrl(API_ENDPOINTS.PROXY_IMAGE)}?path=${encodeURIComponent(path)}&size=${size}`;
        };
        
    const formatDate = (dateString) => {
        if (!dateString) return 'Bilinmiyor';
        
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('tr-TR', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            });
        } catch (error) {
            return 'Geçersiz tarih';
        }
    };

    if (loading) {
        return (
            <AdminContainer>
            <Header>
                <Title>Admin Paneli</Title>
                <SearchContainer>
                    <SearchInput
                        type="text"
                        placeholder="İçerik ara... (başlık, TMDB ID, tür)"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </SearchContainer>
                <AddButton to="/admin/add">Yeni İçerik Ekle</AddButton>
            </Header>
                <LoadingText>İçerikler yükleniyor...</LoadingText>
            </AdminContainer>
        );
    }

    if (error) {
        return (
            <AdminContainer>
                <Header>
                    <Title>Admin Paneli</Title>
                    <AddButton to="/admin/add">Yeni İçerik Ekle</AddButton>
                </Header>
                <ErrorText>
                    {error}
                    <br />
                    <button 
                        onClick={fetchContent}
                        style={{
                            marginTop: '20px',
                            padding: '10px 20px',
                            background: 'var(--gradient-cyan)',
                            border: 'none',
                            borderRadius: '5px',
                            color: '#111',
                            fontWeight: '600',
                            cursor: 'pointer'
                        }}
                    >
                        Tekrar Dene
                    </button>
                </ErrorText>
            </AdminContainer>
        );
    }

    return (
        <AdminContainer>
            <Header>
                <Title>Admin Paneli</Title>
                <SearchContainer>
                    <SearchInput
                        type="text"
                        placeholder="İçerik ara... (başlık, TMDB ID, tür)"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </SearchContainer>
                <AddButton to="/admin/add">Yeni İçerik Ekle</AddButton>
            </Header>
            
            {/* İstatistikler */}
            <StatsContainer>
                <StatCard>
                    <h3>{searchTerm ? filteredContent.length : stats.total}</h3>
                    <p>{searchTerm ? 'Bulunan İçerik' : 'Toplam İçerik'}</p>
                </StatCard>
                <StatCard>
                    <h3>{stats.anime}</h3>
                    <p>Anime</p>
                </StatCard>
                <StatCard>
                    <h3>{stats.tv}</h3>
                    <p>Dizi</p>
                </StatCard>
                <StatCard>
                    <h3>{stats.movie}</h3>
                    <p>Film</p>
                </StatCard>
            </StatsContainer>

            <ContentTable>
                <thead>
                    <tr>
                        <Th>Kapak</Th>
                        <Th>Başlık</Th>
                        <Th>Tür</Th>
                        <Th>TMDB ID</Th>
                        <Th>Çıkış Tarihi</Th>
                        <Th>Eklenme Tarihi</Th>
                        <Th>İşlemler</Th>
                    </tr>
                </thead>
                <tbody>
                    {filteredContent.map(item => (
                        <tr key={item.id}>
                            <Td>
                                <img 
                                src={getPosterUrl(item.poster_path)} 
                                alt={item.title} 
                                onError={(e) => {
                                    e.target.src = '/placeholder-poster.jpg';
                                }}
                                />
                            </Td>
                            <Td>
                                <div style={{ fontWeight: '600' }}>{item.title}</div>
                                {item.original_title && item.original_title !== item.title && (
                                    <div style={{ fontSize: '0.8rem', color: '#b2bec3', marginTop: '5px' }}>
                                        {item.original_title}
                                    </div>
                                )}
                            </Td>
                            <Td>
                                <MediaTypeBadge type={item.media_type}>
                                    {getMediaTypeLabel(item)}
                                </MediaTypeBadge>
                            </Td>
                            <Td>
                                <div style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>
                                    {item.tmdb_id}
                                </div>
                            </Td>
                            <Td>
                                {item.release_date ? formatDate(item.release_date) : 'Belirtilmemiş'}
                            </Td>
                            <Td>
                                {formatDate(item.created_at)}
                            </Td>
                            <Td>
                                <ActionButtons>
                                    <EditButton to={`/admin/edit/${item.id}`}>Düzenle</EditButton>
                                    <DeleteButton onClick={() => handleDelete(item.id, item.title)}>Sil</DeleteButton>
                                </ActionButtons>
                            </Td>
                        </tr>
                    ))}
                </tbody>
            </ContentTable>
            
            {content.length === 0 && (
                <div style={{ 
                    textAlign: 'center', 
                    padding: '50px', 
                    color: '#b2bec3',
                    fontSize: '1.2rem'
                }}>
                    Henüz hiç içerik eklenmemiş. <Link to="/admin/add" style={{ color: '#4ecdc4' }}>İlk içeriği eklemek için tıklayın</Link>.
                </div>
            )}
        </AdminContainer>
    );
};

export default AdminPage;
